import { z } from 'zod'
import { REVIEW_MODES } from '../../../types/review-mode'

export const ReviewModeDtoSchema = z.enum(REVIEW_MODES)

export const ReviewParseWarningDtoSchema = z.object({
  code: z.string(),
  message: z.string(),
  detail: z.string().nullable().optional(),
})

export const ReviewCommentDtoSchema = z.object({
  comment_id: z.string().nullable().optional(),
  paragraph_id: z.string().nullable().optional(),
  search_text: z.string().nullable().optional(),
  comment_text: z.string(),
})

export const ReviewCommentsArrayDtoSchema = z.array(ReviewCommentDtoSchema)

export const MessageSourceDtoSchema = z.object({
  file_id: z.string(),
  filename: z.string(),
  sections: z.record(z.string(), z.string()),
})

export const MessageSourcesDtoSchema = z.array(MessageSourceDtoSchema)

/**
 * Runtime validation schema for the chat completion response.
 */
const ChatCompletionMetadataDtoSchema = z
  .object({
    finish_reason: z.string().optional(),
    comments: ReviewCommentsArrayDtoSchema.optional(),
    parse_warnings: z.array(ReviewParseWarningDtoSchema).optional(),
    model_name: z.string().optional(),
    token_usage: z.record(z.string(), z.unknown()).optional(),
    // Malformed sources never fail the whole response: the raw input is passed through
    // and re-validated by the mapper, which turns it into `undefined` (the UI's error state).
    sources: MessageSourcesDtoSchema.optional().catch((ctx) => ctx.input as never),
  })
  .optional()

export const ChatCompletionResponseDtoSchema = z.object({
  content: z.string().min(1),
  metadata: ChatCompletionMetadataDtoSchema.optional(),
})

const CurrentDocumentContextEntryDtoSchema = z.object({
  type: z.enum([
    'full_document',
    'full_document_paragraph',
    'mouse_selection',
    'section',
    'table_html',
  ]),
  content: z.string(),
  id: z.string().nullable().optional(),
  table_context: z
    .object({
      table_caption: z.string(),
      col_heading: z.string().nullable().optional(),
      row_heading: z.string().nullable().optional(),
    })
    .optional(),
})

const CurrentDocumentCommentEntryDtoSchema = z.object({
  comment_id: z.string(),
  paragraph_id: z.string().optional(),
  search_text: z.string().nullable().optional(),
  comment_text: z.string(),
  author_email: z.string().nullable().optional(),
  author_name: z.string().nullable().optional(),
  creation_date: z.string().nullable().optional(),
  resolved: z.boolean().nullable().optional(),
})

const ChatCompletionContextDtoSchema = z.object({
  filepool_context: z
    .array(
      z.object({
        file_id: z.string(),
        section_ids: z.array(z.string()),
      })
    )
    .optional(),
  current_document_context: z.array(CurrentDocumentContextEntryDtoSchema).optional(),
  current_document_comments: z.array(CurrentDocumentCommentEntryDtoSchema).optional(),
})

export const ChatCompletionRequestDtoSchema = z.object({
  messages: z.array(
    z.object({
      role: z.enum(['user', 'assistant', 'system']),
      content: z.string(),
    })
  ),
  model: z.string(),
  context: ChatCompletionContextDtoSchema,
  stream: z.boolean(),
  review_mode: ReviewModeDtoSchema.optional(),
  response_format: z.string().optional(),
})

export const ModelsResponseDtoSchema = z.array(z.string())

export type ModelsResponseDto = z.infer<typeof ModelsResponseDtoSchema>

export type ChatCompletionResponse = z.infer<typeof ChatCompletionResponseDtoSchema>

export type ReviewCommentDto = z.infer<typeof ReviewCommentDtoSchema>

export { CurrentDocumentCommentEntryDtoSchema }
